import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function HeroSlider({ slides }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const mediaRef = useRef(null);
  const activeSlide = slides[activeIndex];

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (activeIndex >= slides.length) setActiveIndex(0);
  }, [activeIndex, slides.length]);

  useEffect(() => {
    if (paused || reducedMotion || slides.length < 2) return undefined;
    const timer = window.setInterval(() => setActiveIndex((index) => (index + 1) % slides.length), 5200);
    return () => window.clearInterval(timer);
  }, [paused, reducedMotion, slides.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (!mediaRef.current || reducedMotion) return;
      const bounds = mediaRef.current.getBoundingClientRect();
      const shift = Math.max(-12, Math.min(12, (window.innerHeight * 0.5 - bounds.top) * 0.025));
      mediaRef.current.style.setProperty("--parallax", `${shift}px`);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [reducedMotion]);

  const showSlide = (index) => setActiveIndex((index + slides.length) % slides.length);

  if (!activeSlide) {
    return <div className="hero-visual hero-empty" data-testid="hero-inventory-loading"><div className="hero-empty-frame"><span className="section-kicker">LIVE-BESTAND</span><strong>Fahrzeuge werden geladen.</strong><p>Die aktuellen Fotos und Eckdaten kommen direkt aus dem Händlerbestand.</p></div></div>;
  }

  return (
    <div className="hero-visual" aria-label="Fahrzeugauswahl">
      <div className="hero-media-frame" ref={mediaRef} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {slides.map((slide, index) => <img key={slide.id} className={`hero-image ${index === activeIndex ? "is-active" : ""}`} src={slide.image} alt={`${slide.model}, Fahrzeugansicht`} loading={index === 0 ? "eager" : "lazy"} />)}
        <div className="hero-media-index"><span>0{activeIndex + 1}</span><i /> <span>0{slides.length}</span></div>
      </div>
      <div className="hero-visual-footer">
        <div className="hero-vehicle-meta"><span className="mono-label">LIVE-KOLLEKTION · {activeSlide.year}</span><strong>{activeSlide.model}</strong><span>{activeSlide.edition}</span></div>
        <div className="hero-slider-controls">
          <button type="button" aria-label="Vorheriges Fahrzeug" onClick={() => showSlide(activeIndex - 1)} data-testid="hero-slider-prev-button"><ArrowLeft size={17} strokeWidth={1.4} /></button>
          <div className="hero-slider-dots">{slides.map((slide, index) => <button key={slide.id} className={index === activeIndex ? "is-active" : ""} type="button" aria-label={`${slide.model} anzeigen`} aria-current={index === activeIndex} onClick={() => showSlide(index)} data-testid={`hero-slider-dot-${index + 1}-button`} />)}</div>
          <button type="button" aria-label="Nächstes Fahrzeug" onClick={() => showSlide(activeIndex + 1)} data-testid="hero-slider-next-button"><ArrowRight size={17} strokeWidth={1.4} /></button>
        </div>
      </div>
      <div className="hero-telemetry"><div><span>Leistung</span><strong>{activeSlide.power}</strong></div><div><span>Erstzulassung</span><strong>{activeSlide.year}</strong></div><div><span>Laufleistung</span><strong>{activeSlide.mileage}</strong></div><div><span>Preis</span><strong>{activeSlide.price}</strong></div></div>
    </div>
  );
}
